import { Link } from "react-router-dom";
import { Award, Building2, ExternalLink, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

// Sponsors data - First sponsor is Gotor
const sponsors = [
  {
    id: 1,
    name: "GOTOR Comunicaciones",
    description: "Somos una empresa familiar fundada en 1989, basada en la consultoría e ingeniería de telecomunicaciones. Nos dedicamos a diseñar, implementar y mantener soluciones tecnológicas que mejoran la comunicación y eficiencia operativa de las empresas. Con más de 35 años de experiencia en el sector, estamos preparados para una adaptación completa a la hora de resolver problemas, anticipar necesidades, y visualizar futuros horizontes tecnológicos. Somos más que una empresa de telecomunicaciones, somos tus aliados estratégicos en este sector.",
    logo: "/images/gotor-logo.png",
    website: "https://gotor.com/"
  },
  {
    id: 2,
    name: "Grupo Lobe",
    description: "Grupo Lobe es una promotora y constructora de Zaragoza referente en edificación residencial sostenible de obra nueva, tanto en vivienda libre como de protección oficial (VPO). Su principal factor de diferenciación es el compromiso con el estándar Passivhaus y la integración de tecnologías Smart House, garantizando la máxima eficiencia energética y un drástico ahorro en el consumo de los hogares. Destaca por digitalizar e industrializar todo el proceso constructivo bajo un modelo de gestión integral, posicionándose como un socio estratégico clave y un patrocinador de vanguardia enfocado en la innovación y el futuro de la vivienda.",
    logo: "/images/lobe-logo.png",
    website: "https://www.grupolobe.com/"
  },
  {
    id: 3,
    name: "Mecenas 3",
    description: "Descripción del tercer mecenas colaborador del proyecto.",
    logo: null,
    website: null
  },
  {
    id: 4,
    name: "Mecenas 4",
    description: "Descripción del cuarto mecenas y su vinculación con Zaragoza.",
    logo: null,
    website: null
  },
  {
    id: 5,
    name: "Mecenas 5",
    description: "Descripción del quinto mecenas patrocinador del Reloj Solar.",
    logo: null,
    website: null
  }
];

const Patrocinadores = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="patrocinadores-page">
      {/* Header */}
      <header className="bg-rotary-gradient py-16 md:py-24 px-4 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full mb-6">
            <Award className="w-4 h-4 text-[#F7A81B]" />
            <span className="text-white/80 text-sm font-heading uppercase tracking-wider">
              Colaboradores
            </span>
          </div>
          <h1 className="font-bold-sans text-3xl sm:text-5xl md:text-6xl text-white mb-4 tracking-tight break-words">
            Patrocinadores
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Empresas comprometidas con el legado del centenario
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          
          {/* Introduction */}
          <section className="mb-16 text-center" data-testid="patrocinadores-intro">
            <div className="max-w-3xl mx-auto glass-panel p-8 md:p-12 rounded-3xl">
              <div className="divider-gold mb-8" />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-6">
                Mecenazgo del Reloj Solar
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                El Reloj Solar del Rotary Club de Zaragoza es posible gracias al apoyo de empresas 
                comprometidas con el patrimonio cultural y científico de nuestra ciudad.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Cada mecenas tiene su nombre grabado permanentemente en la obra, asociándose para 
                siempre a los valores de Rotary y al legado del centenario.
              </p>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-16 md:mb-24 bg-gradient-to-br from-amber-50/50 to-orange-50/30 -mx-4 px-4 py-12 md:py-16 rounded-3xl" data-testid="benefits-section">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <div className="divider-gold mb-6" />
                <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-4">
                  Beneficios del Mecenazgo
                </h2>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: Sparkles, text: "Nombre grabado en la obra", highlight: true },
                  { icon: Building2, text: "Visibilidad en web y medios" },
                  { icon: Award, text: "Asociación a valores Rotary" },
                  { icon: Heart, text: "Participación en inauguración" },
                  { icon: ExternalLink, text: "Cobertura mediática garantizada" },
                  { icon: Award, text: "Incentivos fiscales" }
                ].map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div 
                      key={index}
                      className={`glass-panel p-5 rounded-xl flex items-center gap-3 transition-all duration-300 ${
                        benefit.highlight ? 'border-[#F7A81B]/30 bg-[#F7A81B]/5' : ''
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        benefit.highlight ? 'bg-[#F7A81B]/20' : 'bg-[#013E7F]/10'
                      }`}>
                        <Icon className={`w-5 h-5 ${benefit.highlight ? 'text-[#F7A81B]' : 'text-[#013E7F]'}`} />
                      </div>
                      <span className="text-slate-700 text-sm font-medium">
                        {benefit.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Sponsors Grid */}
          <section className="mb-16 md:mb-24" data-testid="sponsors-grid">
            <div className="text-center mb-12">
              <div className="divider-gold mb-6" />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-4">
                Nuestros Mecenas
              </h2>
              <p className="text-slate-600">
                Exclusivo: máximo 5 empresas con visibilidad "In Aeternum"
              </p>
            </div>
            
            <div className="space-y-6">
              {sponsors.map((sponsor, index) => (
                <div 
                  key={sponsor.id}
                  className={`sponsor-card p-6 md:p-8 rounded-2xl ${index <= 1 ? 'border-[#F7A81B]/50 bg-gradient-to-r from-amber-50/50 to-white' : ''}`}
                  data-testid={`sponsor-card-${sponsor.id}`}
                >
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    {/* Logo */}
                    <div className={`w-32 h-32 flex-shrink-0 rounded-2xl flex items-center justify-center ${
                      sponsor.logo ? 'bg-white' : 'bg-slate-100 border-2 border-dashed border-slate-300'
                    }`}>
                      {sponsor.logo ? (
                        <img 
                          src={sponsor.logo} 
                          alt={sponsor.name}
                          className="w-full h-full object-contain p-3"
                        />
                      ) : (
                        <Building2 className="w-12 h-12 text-slate-400" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <h3 className="font-heading text-xl font-semibold text-slate-800 break-words">
                          {sponsor.name}
                        </h3>
                        {index <= 1 && (
                          <span className="bg-[#F7A81B] text-slate-900 text-xs px-2 py-1 rounded-full font-heading whitespace-nowrap">
                            Patrocinador Principal
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4 break-words overflow-wrap-anywhere">
                        {sponsor.description}
                      </p>
                      
                      {sponsor.website && (
                        <a
                          href={sponsor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#013E7F] hover:text-[#002D5C] font-medium text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Visitar web
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Investment Info */}
          <section className="mb-16 md:mb-24">
            <div className="glass-countdown rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-eclipse-glow opacity-30" />
              <div className="relative z-10">
                <h3 className="font-accent text-2xl md:text-3xl text-white mb-6">
                  Inversión para el Legado
                </h3>
                <div className="grid sm:grid-cols-3 gap-6 mb-8">
                  <div className="glass-dark rounded-xl p-4">
                    <p className="text-white/50 text-xs mb-1">Valor de la Obra</p>
                    <p className="text-white font-heading text-2xl">50.000€</p>
                  </div>
                  <div className="glass-dark rounded-xl p-4">
                    <p className="text-white/50 text-xs mb-1">Aportación Mínima</p>
                    <p className="text-white font-heading text-2xl">5.000€</p>
                    <p className="text-white/50 text-xs">+ IVA</p>
                  </div>
                  <div className="glass-dark rounded-xl p-4">
                    <p className="text-white/50 text-xs mb-1">Plazas Exclusivas</p>
                    <p className="text-white font-heading text-2xl">5</p>
                    <p className="text-white/50 text-xs">mecenas máximo</p>
                  </div>
                </div>
                <p className="text-white/60 text-sm">
                  Una oportunidad única de asociar tu marca a un legado permanente
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <div className="glass-panel p-8 md:p-12 rounded-3xl bg-gradient-to-br from-amber-50/50 to-white">
              <h3 className="font-heading text-xl md:text-2xl text-slate-800 mb-4">
                ¿Quieres ser parte de este legado?
              </h3>
              <p className="text-slate-600 mb-8 max-w-xl mx-auto">
                Contacta con nosotros para conocer más sobre las oportunidades de mecenazgo 
                y cómo tu empresa puede formar parte de la historia.
              </p>
              <Link to="/contacto" data-testid="patrocinadores-contact-btn">
                <Button className="btn-primary px-10 py-6 rounded-xl text-lg">
                  <Heart className="w-5 h-5 mr-2" />
                  Contactar para Mecenazgo
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Patrocinadores;
